//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material'
import React, { ReactElement, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import EditField from '@react/components/elements/EditField'
// import { useAddLiquidityPoolMutation, useUpdateLiquidityPoolMutation } from '@react/redux/restApi/liquidityPoolApi'
import { yupEthAddrRequired } from '@shared/Enums'

import { EthAddr, ETH_ADDR_ZERO, StringTypeEnum, toFieldMetaData } from '@shared/SchemaTypes'
import * as yup from 'yup'
import { InferType } from 'yup'
import { onPromise } from '@react/components/UiHelpers'

import { toErc20Wei } from '@shared/conversions'

import { useOtChainState, useOtDispatch } from '@react/redux/store'
import { createPoolTx } from '@react/signedTxs/createPoolTx'

import { logOT } from '@shared/loggingShared'
import { AssetImage } from '@react/components/icons/AssetImage'
import { LiquidityAsset } from '@shared/LiquidityAsset'

export const PoolCreateSchema = yup.object({
  tokenSymbol: yup
    .string()
    .required()
    .label('Token Symbol')
    .meta(toFieldMetaData({ propName: 'tokenSymbol' })),
  // PoolDTO
  addr: yupEthAddrRequired
    .label('Pool Contract Address')
    .meta(toFieldMetaData({ propName: 'addr', stringType: StringTypeEnum.STABLE_COINS, offChain: true })),
  poolName: yup
    .string()
    .required()
    .label('Vault Name')
    .meta(toFieldMetaData({ propName: 'poolName' })),
  tenor: yup
    .number()
    .required()
    .positive()
    .label('Tenor')
    .meta(toFieldMetaData({ propName: 'tenor', stringType: StringTypeEnum.TIME_INTERVAL_DAYS })),
  currency: yup
    .mixed<LiquidityAsset>()
    // .oneOf(Object.values(CurrencyEnum))
    // .default(CurrencyEnum.USD)
    .label('Currency')
    .meta(toFieldMetaData({ propName: 'currency', stringType: StringTypeEnum.STABLE_COIN_DEFINITION })),

  maxCapacity: yup
    .number()
    .label('maxCapacity')
    .default(100000000)
    .meta(
      toFieldMetaData({
        propName: 'maxCapacity',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  endDate: yup
    .string()
    .label('endDate')
    .default(Math.round(new Date(2050, 1, 1).getTime() / 1000).toString())
    .meta(toFieldMetaData({ propName: 'endDate' })),

  closeOfBusinessHour: yup
    .number()
    .required()
    .positive()
    .label('Close of Business Hour')
    .meta(toFieldMetaData({ propName: 'closeOfBusinessHour', stringType: StringTypeEnum.TIME_INTERVAL_HOURS })),
  borrowerAddr: yupEthAddrRequired.label('Borrower Manager Addr').meta(
    toFieldMetaData({
      propName: 'borrowerAddr',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  borrowerWalletAddr: yupEthAddrRequired.label('Borrower Wallet Addr').meta(
    toFieldMetaData({
      propName: 'borrowerWalletAddr',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  fiatGatewayAddress: yupEthAddrRequired.label('Fiat gateway Addr').meta(
    toFieldMetaData({
      propName: 'fiatGatewayAddress',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),

  feeCollectorAddress: yupEthAddrRequired.label('Fee Collector Addr').meta(
    toFieldMetaData({
      propName: 'feeCollectorAddress',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  earlyWithdrawFeeBps: yup
    .number()
    .required()
    .default(100)
    .label('earlyWithdrawFeeBps')
    .meta(toFieldMetaData({ propName: 'earlyWithdrawFeeBps' })),
})

export interface PoolCreateFormData extends InferType<typeof PoolCreateSchema> {
  tokenSymbol: string
  maxCapacity: number
  endDate: string
  addr: EthAddr
  borrowerAddr: EthAddr
  borrowerWalletAddr: EthAddr
  closeOfBusinessHour: number
  earlyWithdrawFeeBps: number
  asset: LiquidityAsset
}

export const PoolCreateFormDataDefaultObject = (): PoolCreateFormData => {
  return {
    tokenSymbol: '',
    addr: ETH_ADDR_ZERO,
    poolName: '',
    tenor: 28,
    currency: undefined,
    maxCapacity: 100000000,
    endDate: Math.round(new Date(2050, 1, 1).getTime() / 1000).toString(),
    closeOfBusinessHour: 18,
    borrowerAddr: '',
    borrowerWalletAddr: '',
    earlyWithdrawFeeBps: 100,
    fiatGatewayAddress: ETH_ADDR_ZERO,
    feeCollectorAddress: ETH_ADDR_ZERO,
    asset: {
      chainConfigName: '',
      name: '',
      symbol: '',
      addr: ETH_ADDR_ZERO,
      decimals: 6,
    },
  }
}
export const poolConfigSettingsFormat = (poolFormData: PoolCreateFormData): string[] => {
  return [
    toErc20Wei(poolFormData.maxCapacity),
    poolFormData.endDate,
    poolFormData.borrowerAddr,
    poolFormData.borrowerWalletAddr,
    (poolFormData.closeOfBusinessHour * 3600).toString(),
    poolFormData.earlyWithdrawFeeBps.toString(),
  ]
}

export default function PoolCreateForm(): ReactElement {
  const {
    chainConfiguration,
    chainConfiguration: { perimeterAddresses },
  } = useOtChainState()

  const initialValues = PoolCreateFormDataDefaultObject()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const dispatch = useOtDispatch()

  const SUPPORTED_ASSETS: LiquidityAsset[] = (perimeterAddresses && perimeterAddresses.liquidityAssets) ?? []

  const [selectedAsset, setSelectedAsset] = useState(SUPPORTED_ASSETS[0])

  const handleAssetChange = (event: SelectChangeEvent) => {
    setSelectedAsset(
      SUPPORTED_ASSETS.find((asset: LiquidityAsset) => asset.symbol === event.target.value) ?? SUPPORTED_ASSETS[0]
    )
  }

  const theme = useTheme()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PoolCreateFormData>({
    defaultValues: initialValues,
    resolver: yupResolver(PoolCreateSchema),
  })

  // const [addLiquidityPool] = useAddLiquidityPoolMutation()

  const onSubmit: SubmitHandler<PoolCreateFormData> = (poolFormData: PoolCreateFormData): void => {
    logOT.debug('Submit poolFormData', poolFormData)

    setIsSubmitted(true)

    logOT.debug('SUBMIT New Pool')

    const poolConfigSettings = poolConfigSettingsFormat(poolFormData)

    logOT.info('poolConfigSettings', poolConfigSettings, poolFormData)
    const name = poolFormData.poolName
    const tokenSymbol = poolFormData.tokenSymbol
    logOT.info(' Pool Create Before dispatch')

    dispatch(
      createPoolTx({
        poolConfigSettings,
        name,
        tokenSymbol,
        liquidityAssetAddress: selectedAsset.addr,
      })
    )

    logOT.info(' Pool Create after dispatch')
  }

  logOT.debug('errors', errors)
  return (
    <div data-testid="POOL_CREATE_FORM">
      <form onSubmit={onPromise(handleSubmit(onSubmit))}>
        <Paper>
          {isSubmitted ? <div data-testid="IS_SUBMITTED">IS_SUBMITTED</div> : <div></div>}
          <Box px={3} py={2}>
            <Typography variant="bodyBold" align="center" margin="dense">
              Create a new Investment Pool
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12} sm={12}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolCreateSchema.describe().fields.poolName}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolCreateSchema.describe().fields.tokenSymbol}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolCreateSchema.describe().fields.closeOfBusinessHour}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolCreateSchema.describe().fields.earlyWithdrawFeeBps}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  currency={selectedAsset.symbol}
                  fieldSchema={PoolCreateSchema.describe().fields.maxCapacity}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolCreateSchema.describe().fields.borrowerAddr}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolCreateSchema.describe().fields.borrowerWalletAddr}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl sx={{ m: 1, width: 300 }}>
                  <InputLabel id="asset-label">Asset</InputLabel>
                  <Select
                    labelId="asset-label"
                    id="asset-select"
                    input={<OutlinedInput id="asset-select" label="Asset" />}
                    value={selectedAsset.symbol}
                    renderValue={(assetSymbol: string) => {
                      const asset =
                        SUPPORTED_ASSETS.find((asset: LiquidityAsset) => asset.symbol === assetSymbol) ??
                        SUPPORTED_ASSETS[0]
                      return (
                        <div
                          style={{
                            flexDirection: 'row',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ marginTop: '4px' }}>
                            <AssetImage asset={asset.symbol} />
                          </div>
                          <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                            {asset.symbol}
                          </Typography>
                        </div>
                      )
                    }}
                    onChange={handleAssetChange}
                  >
                    {SUPPORTED_ASSETS.map((asset: LiquidityAsset) => (
                      <MenuItem
                        value={asset.symbol}
                        key={`asset-option-${asset.symbol}`}
                        style={{
                          paddingLeft: 12,
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ marginTop: '4px' }}>
                          <AssetImage asset={asset.symbol} />
                        </div>
                        <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                          {asset.symbol}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                {!isSubmitted && !!chainConfiguration && (
                  <Button
                    sx={{
                      backgroundColor: theme.palette.fg.main,
                      color: '#FFFFFF',
                      padding: '11px 16px 11px 16px',
                      borderRadius: '6px',
                    }}
                    type="submit"
                    data-testid={'submit'}
                    variant="contained"
                  >
                    Submit
                  </Button>
                )}
                {!chainConfiguration && (
                  <Typography variant="bodyBold" align="center" margin="dense">
                    Please connect your wallet to submit a pool
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </form>
    </div>
  )
}
